"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import {
  createCategoryAction,
  updateCategoryAction,
  deleteCategoryAction,
} from "./_actions";

type Category = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  order: number;
  metaTitle: string | null;
  metaDesc: string | null;
  articleCount: number;
};

export function CategoryRow({ cat }: { cat: Category }) {
  const router = useRouter();
  const [editing, setEditing] = useState(false);
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  function save(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    startTransition(async () => {
      const res = await updateCategoryAction(cat.id, fd);
      if (!res.ok) setError(res.error);
      else {
        setEditing(false);
        router.refresh();
      }
    });
  }

  function remove() {
    if (
      !confirm(
        cat.articleCount > 0
          ? `Chuyên mục "${cat.name}" còn ${cat.articleCount} bài. Xóa sẽ thất bại — bạn vẫn muốn thử?`
          : `Xóa chuyên mục "${cat.name}"?`,
      )
    )
      return;
    setError(null);
    startTransition(async () => {
      const res = await deleteCategoryAction(cat.id);
      if (!res.ok) setError(res.error);
      else router.refresh();
    });
  }

  if (editing) {
    return (
      <tr className="border-b border-stroke-subtle/60">
        <td colSpan={5} className="p-4 bg-bg-elevated/30">
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-3">
            {error && (
              <div className="sm:col-span-2 text-sm text-rose-300">{error}</div>
            )}
            <label className="text-xs">
              Tên
              <input
                name="name"
                defaultValue={cat.name}
                required
                className="mt-1 w-full px-2.5 py-1.5 rounded bg-bg-elevated border border-stroke-subtle text-sm"
              />
            </label>
            <label className="text-xs">
              Slug
              <input
                name="slug"
                defaultValue={cat.slug}
                required
                className="mt-1 w-full px-2.5 py-1.5 rounded bg-bg-elevated border border-stroke-subtle text-sm font-mono"
              />
            </label>
            <label className="text-xs sm:col-span-2">
              Mô tả
              <textarea
                name="description"
                defaultValue={cat.description ?? ""}
                rows={2}
                className="mt-1 w-full px-2.5 py-1.5 rounded bg-bg-elevated border border-stroke-subtle text-sm"
              />
            </label>
            <label className="text-xs">
              Meta title
              <input
                name="metaTitle"
                defaultValue={cat.metaTitle ?? ""}
                className="mt-1 w-full px-2.5 py-1.5 rounded bg-bg-elevated border border-stroke-subtle text-sm"
              />
            </label>
            <label className="text-xs">
              Order
              <input
                name="order"
                type="number"
                defaultValue={cat.order}
                className="mt-1 w-full px-2.5 py-1.5 rounded bg-bg-elevated border border-stroke-subtle text-sm"
              />
            </label>
            <label className="text-xs sm:col-span-2">
              Meta description
              <textarea
                name="metaDesc"
                defaultValue={cat.metaDesc ?? ""}
                rows={2}
                className="mt-1 w-full px-2.5 py-1.5 rounded bg-bg-elevated border border-stroke-subtle text-sm"
              />
            </label>
            <div className="sm:col-span-2 flex gap-2 justify-end pt-2">
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setError(null);
                }}
                className="px-3 py-1.5 rounded text-sm hover:bg-bg-overlay"
              >
                Hủy
              </button>
              <button
                type="submit"
                disabled={pending}
                className="px-4 py-1.5 rounded bg-accent-primary text-white text-sm font-medium disabled:opacity-50"
              >
                {pending ? "Đang lưu…" : "Lưu"}
              </button>
            </div>
          </form>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-stroke-subtle/60 last:border-0 hover:bg-bg-elevated/30 transition">
      <td className="px-4 py-3">
        <div className="font-medium">{cat.name}</div>
        {cat.description && (
          <div className="text-xs text-ink-muted line-clamp-1 mt-0.5">{cat.description}</div>
        )}
      </td>
      <td className="px-4 py-3">
        <code className="text-xs px-1.5 py-0.5 rounded bg-bg-overlay font-mono">{cat.slug}</code>
      </td>
      <td className="px-4 py-3 text-sm text-ink-secondary">{cat.articleCount}</td>
      <td className="px-4 py-3 text-sm text-ink-muted">{cat.order}</td>
      <td className="px-4 py-3 text-right">
        {error && <div className="text-xs text-rose-300 mb-1">{error}</div>}
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-xs text-accent-primary hover:underline mr-3"
        >
          Sửa
        </button>
        <button
          type="button"
          onClick={remove}
          disabled={pending}
          className="text-xs text-rose-300 hover:underline disabled:opacity-50"
        >
          Xóa
        </button>
      </td>
    </tr>
  );
}

export function CategoryCreateForm() {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    startTransition(async () => {
      const res = await createCategoryAction(fd);
      if (!res.ok) setError(res.error);
      else {
        (e.target as HTMLFormElement).reset();
        setOpen(false);
        router.refresh();
      }
    });
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="px-4 py-2 rounded-lg bg-accent-primary text-white text-sm font-medium hover:bg-accent-primary/90 transition"
      >
        + Tạo chuyên mục
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-stroke-subtle bg-bg-elevated/40 p-5 space-y-3 mb-6">
      <h2 className="font-display font-semibold">Chuyên mục mới</h2>
      {error && (
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
          {error}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs">
          Tên *
          <input
            name="name"
            required
            className="mt-1 w-full px-3 py-2 rounded-lg bg-bg-elevated border border-stroke-subtle text-sm"
            placeholder="VD: Chứng khoán"
          />
        </label>
        <label className="text-xs">
          Slug <span className="text-ink-muted/60">(tự sinh nếu trống)</span>
          <input
            name="slug"
            className="mt-1 w-full px-3 py-2 rounded-lg bg-bg-elevated border border-stroke-subtle text-sm font-mono"
            placeholder="chung-khoan"
          />
        </label>
        <label className="text-xs sm:col-span-2">
          Mô tả
          <textarea
            name="description"
            rows={2}
            className="mt-1 w-full px-3 py-2 rounded-lg bg-bg-elevated border border-stroke-subtle text-sm"
          />
        </label>
        <label className="text-xs">
          Order
          <input
            name="order"
            type="number"
            defaultValue={0}
            className="mt-1 w-full px-3 py-2 rounded-lg bg-bg-elevated border border-stroke-subtle text-sm"
          />
        </label>
      </div>
      <div className="flex gap-2 justify-end pt-2">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="px-4 py-1.5 rounded text-sm hover:bg-bg-overlay"
        >
          Hủy
        </button>
        <button
          type="submit"
          disabled={pending}
          className="px-4 py-1.5 rounded bg-accent-primary text-white text-sm font-medium disabled:opacity-50"
        >
          {pending ? "Đang tạo…" : "Tạo"}
        </button>
      </div>
    </form>
  );
}
